import { createContext, createElement, useCallback, useContext, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';

import { createHubFactory } from '../realtime/hubFactory';
import type { PortalHubConnection } from '../realtime/hubFactory';
import type { AppActivity, NetworkAvailability } from '../ports';
import type {
  ChatId,
  ChatMessage,
  Incident,
  IncidentAppleEedCapabilities,
  IncidentId,
  IncidentLiveState,
  PortalHubClientEvents,
} from '../realtime/contracts';
import { CallerConnectedStatus, WebAppState } from '../realtime/contracts';
import { PortalClient } from './portalClient';
import { IncidentStore } from './incidentStore';
import type { IncidentsSnapshot } from './incidentStore';
import { CallStore } from './callStore';
import type { CallsSnapshot } from './callStore';
import { ConnectionStateTracker } from './connectionStateTracker';
import type { RealtimeConnectionState } from './connectionStateTracker';
import { startRealtimeLifecycle } from './realtimeLifecycle';
import { selectRealtimeStatusMessage } from './realtimeStatusMessage';
import type { RealtimeStatusMessage } from './realtimeStatusMessage';

const PortalClientContext = createContext<PortalClient | null>(null);
const NetworkAvailabilityContext = createContext<NetworkAvailability | null>(null);

export type PortalClientProviderProps = {
  emsServerUrl: string;
  accessTokenFactory: () => Promise<string>;
  networkAvailability?: NetworkAvailability;
  appActivity?: AppActivity;
  onResume?: () => void;
  onError?: (message: string, error: unknown) => void;
  children: ReactNode;
};

export function PortalClientProvider({
  emsServerUrl,
  accessTokenFactory,
  networkAvailability,
  appActivity,
  onResume,
  onError,
  children,
}: PortalClientProviderProps) {
  const [client, setClient] = useState<PortalClient | null>(null);

  const dependencies = useRef({ accessTokenFactory, onResume, onError });
  useEffect(() => {
    dependencies.current = { accessTokenFactory, onResume, onError };
  });

  useEffect(() => {
    const hub = createHubFactory({
      emsServerUrl,
      accessTokenFactory: () => dependencies.current.accessTokenFactory(),
    }).createPortalHubConnection();

    const created = new PortalClient({
      hub,
      onError: (message, error) => dependencies.current.onError?.(message, error),
    });

    setClient(created);

    return () => {
      setClient(null);
      created.dispose();
    };
  }, [emsServerUrl]);

  useEffect(() => {
    if (client === null) {
      return;
    }

    return startRealtimeLifecycle({
      client,
      networkAvailability,
      appActivity,
      onResume: () => dependencies.current.onResume?.(),
    });
  }, [client, networkAvailability, appActivity]);

  return createElement(
    NetworkAvailabilityContext.Provider,
    { value: networkAvailability ?? null },
    createElement(PortalClientContext.Provider, { value: client }, children),
  );
}

export function usePortalClient(): PortalClient | null {
  return useContext(PortalClientContext);
}

function useStoreSnapshot<TSnapshot>(
  store: { subscribe: (listener: () => void) => () => void; getSnapshot: () => TSnapshot } | undefined,
  fallback: TSnapshot,
): TSnapshot {
  return useSyncExternalStore(
    (listener) => store?.subscribe(listener) ?? (() => undefined),
    () => store?.getSnapshot() ?? fallback,
    () => store?.getSnapshot() ?? fallback,
  );
}

export function useIncidents(): IncidentsSnapshot {
  return useStoreSnapshot(usePortalClient()?.incidents, IncidentStore.emptySnapshot);
}

export function useCalls(): CallsSnapshot {
  return useStoreSnapshot(usePortalClient()?.calls, CallStore.emptySnapshot);
}

export function useRealtimeConnection(): RealtimeConnectionState {
  return useStoreSnapshot(usePortalClient()?.connection, ConnectionStateTracker.neverConnectedSnapshot);
}

export function useNetworkIsOnline(): boolean {
  const networkAvailability = useContext(NetworkAvailabilityContext);

  const subscribe = useCallback(
    (listener: () => void) => networkAvailability?.subscribe(() => listener()) ?? (() => undefined),
    [networkAvailability],
  );
  const getSnapshot = useCallback(() => networkAvailability?.isOnline() ?? true, [networkAvailability]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useRealtimeStatusMessage(): RealtimeStatusMessage {
  return selectRealtimeStatusMessage(useRealtimeConnection(), useNetworkIsOnline());
}

export function useIncidentSelector<TSelected>(
  incidentId: IncidentId | null,
  select: (incident: Incident | null) => TSelected,
): TSelected {
  const store = usePortalClient()?.incidents;

  const subscribe = useCallback(
    (listener: () => void) => store?.subscribe(listener) ?? (() => undefined),
    [store],
  );
  const getSnapshot = useCallback(
    () => select(incidentId === null ? null : (store?.getById(incidentId) ?? null)),
    [store, incidentId, select],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

const selectIncident = (incident: Incident | null): Incident | null => incident;

export function useIncident(incidentId: IncidentId | null): Incident | null {
  return useIncidentSelector(incidentId, selectIncident);
}

export type UseIncidentChatResult = {
  chatId: ChatId | null;
  messages: readonly ChatMessage[] | null;
  isLoaded: boolean;
};

const selectChatId = (incident: Incident | null): ChatId | null => incident?.chatId ?? null;
const selectChatMessages = (incident: Incident | null): readonly ChatMessage[] | null =>
  incident?.chatMessages ?? null;

export function useIncidentChat(incidentId: IncidentId | null): UseIncidentChatResult {
  const chatId = useIncidentSelector(incidentId, selectChatId);
  const messages = useIncidentSelector(incidentId, selectChatMessages);
  const isLoaded = useIncidentSelector(incidentId, selectIncident) !== null;

  return { chatId, messages, isLoaded };
}

export const UNKNOWN_INCIDENT_LIVE_STATE: IncidentLiveState = {
  callerConnectedStatus: CallerConnectedStatus.NotConnectedYet,
  webAppState: WebAppState.None,
  allowChatTranslations: false,
  callerMessagePreview: null,
  callerIsSendingImage: false,
};

const selectLiveState = (incident: Incident | null): IncidentLiveState | null => incident?.liveState ?? null;

export function useIncidentLiveState(incidentId: IncidentId | null): IncidentLiveState {
  return useIncidentSelector(incidentId, selectLiveState) ?? UNKNOWN_INCIDENT_LIVE_STATE;
}

const NO_APPLE_EED_CAPABILITIES: readonly IncidentAppleEedCapabilities[] = [];

const selectAppleEedCapabilities = (incident: Incident | null): readonly IncidentAppleEedCapabilities[] | null =>
  incident?.appleEedCapabilities ?? null;

export function useIncidentAppleEedCapabilities(incidentId: IncidentId | null): readonly IncidentAppleEedCapabilities[] {
  return useIncidentSelector(incidentId, selectAppleEedCapabilities) ?? NO_APPLE_EED_CAPABILITIES;
}

export type IncidentDetailStatus = 'loading' | 'loaded' | 'not-found' | 'failed';

export type UseIncidentDetailResult = {
  incident: Incident | null;
  status: IncidentDetailStatus;
  retry: () => void;
};

export function useIncidentDetail(incidentId: IncidentId | null): UseIncidentDetailResult {
  const client = usePortalClient();
  const { dataState, incidentsById, notFoundIds, failedIds } = useIncidents();
  const incident = incidentId === null ? null : (incidentsById.get(incidentId) ?? null);
  const incidentIdsBeingRead = useRef(new Set<IncidentId>());

  const missingFromLiveWindow = incidentId !== null && incident === null && dataState !== 'loading';

  useEffect(() => {
    if (client === null || incidentId === null) {
      return;
    }

    client.retainIncident(incidentId);

    return () => client.releaseIncident(incidentId);
  }, [client, incidentId]);

  useEffect(() => {
    if (client === null || incidentId === null || !missingFromLiveWindow) {
      return;
    }
    if (incidentIdsBeingRead.current.has(incidentId) || failedIds.has(incidentId) || notFoundIds.has(incidentId)) {
      return;
    }

    incidentIdsBeingRead.current.add(incidentId);
    void client.loadIncident(incidentId).finally(() => incidentIdsBeingRead.current.delete(incidentId));
  }, [client, incidentId, missingFromLiveWindow, failedIds, notFoundIds]);

  const retry = useCallback(() => {
    if (client !== null && incidentId !== null) {
      void client.loadIncident(incidentId);
    }
  }, [client, incidentId]);

  if (incident !== null) {
    return { incident, status: 'loaded', retry };
  }

  return {
    incident: null,
    status: statusOfMissingIncident(incidentId, notFoundIds, failedIds),
    retry,
  };
}

function statusOfMissingIncident(
  incidentId: IncidentId | null,
  notFoundIds: ReadonlySet<IncidentId>,
  failedIds: ReadonlySet<IncidentId>,
): IncidentDetailStatus {
  if (incidentId === null) {
    return 'loading';
  }
  if (notFoundIds.has(incidentId)) {
    return 'not-found';
  }

  return failedIds.has(incidentId) ? 'failed' : 'loading';
}

type IncidentScopedEvent = {
  [TEventName in keyof PortalHubClientEvents]: Parameters<PortalHubClientEvents[TEventName]>[0] extends IncidentId
    ? TEventName
    : never;
}[keyof PortalHubClientEvents];

type WithoutIncidentId<TParameters extends unknown[]> = TParameters extends [IncidentId, ...infer TRest]
  ? TRest
  : never;

export function useIncidentHubEvent<TEventName extends IncidentScopedEvent>(
  incidentId: IncidentId | null,
  eventName: TEventName,
  handler: (...args: WithoutIncidentId<Parameters<PortalHubClientEvents[TEventName]>>) => void,
): void {
  const hub = usePortalHub();
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    if (hub === null || incidentId === null) {
      return;
    }

    return hub.on(eventName, ((eventIncidentId: IncidentId, ...args: unknown[]) => {
      if (eventIncidentId === incidentId) {
        (handlerRef.current as (...handlerArgs: unknown[]) => void)(...args);
      }
    }) as PortalHubClientEvents[TEventName]);
  }, [hub, incidentId, eventName]);
}

export function usePortalHub(): PortalHubConnection | null {
  return usePortalClient()?.hubConnection ?? null;
}
